import { useEffect } from 'react';
import { useRouter } from 'next/router';
import SidebarLayout from 'layouts/SidebarLayout';
import Sidescreen from 'components/Sidescreen';
import { profile } from 'lang/en.json';
import styles from 'styles/modules/mediaCoverage/Index.module.scss';
import Button from 'components/Button';
import { useDispatch, useSelector } from 'react-redux';
import EmptyBlock from 'components/EmptyBlock';
import pageTitle from 'utils/seo-title';
import { notFound, notFoundOrThrow } from 'utils/isr';
import { buildCoverageListSeo } from 'utils/profile-seo';

import {
    handleHidePopup,
    handleHideSidescreen,
    handleShowPopup,
    handleShowSidescreen,
    setPopupConfig,
    setPopupLoading,
} from 'store/ux';
import {
    editCoverage,
    fetchCoverages,
    pinCoverage,
    removeCoverage,
    seeCoverages,
} from 'store/company/coverage';
import Card from 'components/Card';
import MissedCoverage from 'components/Profile/MissedCoverage';
import { companySelector, fetchCompanyBySlug } from 'store/company';
import SEO from 'next-seo.config';
import { NextSeo } from 'next-seo';
import { wrapper } from 'store';
import { authSelector, handleAuthorization } from 'store/auth';
import Typo from 'components/Typo';
import { MAX_PAGE_SIZE } from 'data/globalConstants';

export default function Coverages({ seo }) {
    const {
        data: { name, slug: companySlug, type },
    } = useSelector(companySelector);
    const { isAuthorized, isAdmin, isAuthenticated, company } =
        useSelector(authSelector);
    const coverages = useSelector(({ company }) => company.coverages.data);
    const dispatch = useDispatch();
    const router = useRouter();

    // The page is cached HTML built without a user, so who is looking is
    // resolved here instead of in getStaticProps.
    useEffect(() => {
        if (isAuthenticated) {
            dispatch(handleAuthorization(router.query.company));
        }
    }, [isAuthenticated, router.query.company]);

    const handleAddCoverage = () => {
        dispatch(handleShowSidescreen('add-mediaCoverage'));
    };

    const handleEditCoverage = (id) => {
        dispatch(editCoverage(coverages.find((item) => item.id === id)));
        dispatch(handleShowSidescreen('edit-mediaCoverage'));
    };

    const newCoverageButton = isAdmin ? (
        <Button
            onClick={handleAddCoverage}
            classes={[styles.button]}
            title={profile.media_coverage.button}
        />
    ) : (
        <MissedCoverage className={styles.tellUs} />
    );

    const handleRemovePopup = (id) => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                onConfirm: () => handleRemoveConfirm(id),
                onCancel: handleRemoveCancel,
                title: 'Delete Media coverage?',
                confirmTitle: 'Confirm',
                cancelTitle: 'Cancel',
            })
        );
    };

    const handleRemoveConfirm = async (id) => {
        dispatch(setPopupLoading(true));
        await dispatch(removeCoverage(id));
        dispatch(setPopupLoading(false));
        dispatch(handleHidePopup());
    };

    const handleRemoveCancel = () => {
        dispatch(handleHidePopup());
    };

    const handlePin = (id) => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                onConfirm: () => handlePinConfirm(id),
                onCancel: handleRemoveCancel,
                title: 'Change pin status of the media coverage?',
                confirmTitle: 'Confirm',
                cancelTitle: 'Cancel',
            })
        );
    };

    const handlePinConfirm = async (id) => {
        dispatch(setPopupLoading(true));
        await dispatch(pinCoverage(id));
        dispatch(setPopupLoading(false));
        dispatch(handleHidePopup());
    };

    const published = coverages?.length > 0 && (
        <>
            <div className={styles.cardBlocks}>
                {coverages.map((item, key) => (
                    <Card
                        pretitle={item.publisher}
                        quotedOn={item.alert?.title}
                        title={item.title}
                        publishedAt={item.createdAt}
                        key={`SingleCard-item-${key}`}
                        link={item.url}
                        onRemove={isAuthorized && handleRemovePopup}
                        onEdit={isAdmin && handleEditCoverage}
                        editLabel={profile.media_coverage.edit.button}
                        cta={'Read more'}
                        id={item.id}
                        titleLimit={120}
                        status={item.status}
                        onApprove={
                            companySlug === company && isAuthorized && handlePin
                        }
                    />
                ))}
            </div>
        </>
    );

    const handleMarkCoveragesAsSeen = async () => {
        await dispatch(seeCoverages());
    };

    useEffect(() => {
        if (
            isAuthenticated &&
            company === companySlug &&
            coverages?.some(({ status }) => status === 'new')
        ) {
            handleMarkCoveragesAsSeen();
        }
    }, [isAuthenticated, company, companySlug]);

    const isEmpty = coverages?.length < 1;

    return (
        <>
            <NextSeo
                {...SEO}
                {...seo}
                titleTemplate={
                    seo?.title || pageTitle(`${name} Media Coverage`)
                }
                noindex={isEmpty || type === 'journalist'}
                openGraph={{ ...SEO.openGraph, ...seo?.openGraph }}
            />
            <SidebarLayout isAuthenticated={isAuthenticated}>
                <div className={styles.wrapper}>
                    <div className={['mb-l', styles.titleWrapper].join(' ')}>
                        <Typo variant={'h1'}>
                            {isAuthorized
                                ? profile.media_coverage.title
                                : `${name}'s Media Coverage`}
                        </Typo>
                        {newCoverageButton}
                    </div>
                    {published}
                    {isEmpty && (
                        <EmptyBlock
                            title={profile.media_coverage.placeholder_empty}
                        />
                    )}
                </div>
                <Sidescreen
                    closeFn={handleHideSidescreen}
                    onUpdate={() => {
                        dispatch(handleHideSidescreen());
                    }}
                />
            </SidebarLayout>
        </>
    );
}

export const getStaticPaths = () => ({ paths: [], fallback: 'blocking' });

export const getStaticProps = wrapper.getStaticProps(
    (store) =>
        async ({ params }) => {
            try {
                await store.dispatch(fetchCompanyBySlug(params.company));
            } catch (err) {
                return notFoundOrThrow(err);
            }

            const profile = store.getState().company.data;

            if (!profile?.id || profile.deactivated) {
                return notFound();
            }

            await store.dispatch(fetchCoverages({ size: MAX_PAGE_SIZE }));

            const { coverages } = store.getState().company;

            return {
                props: {
                    data: coverages.data,
                    seo: buildCoverageListSeo(
                        profile,
                        process.env.HOST,
                        coverages.data ?? []
                    ),
                },
                revalidate: 86400,
            };
        }
);
